import { useEffect, useState } from "react";
import { deleteOrder, getOrders, updateOrderStatus } from "../../../api/apiServices";
import AdminSidebar from "../components/AdminSidebar";
import AdminHeader from "../components/AdminHeader";

export default function OrdersManager() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  /* -------------------- LOAD ORDERS -------------------- */
  const loadOrders = async () => {
    try {
      setLoading(true);
      const res = await getOrders();
      setOrders(res || []);
    } catch (err) {
      console.error("Failed to load orders:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  /* -------------------- FILTER -------------------- */
  useEffect(() => {
    let result = [...orders];

    if (search) {
      result = result.filter((o) =>
        o._id.toLowerCase().includes(search.toLowerCase()) ||
        o.email?.toLowerCase().includes(search.toLowerCase()) ||
        o.firstName?.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (statusFilter !== "all") {
      result = result.filter((o) => o.status === statusFilter);
    }

    setFiltered(result);
  }, [search, statusFilter, orders]);

  /* -------------------- UPDATE STATUS -------------------- */
  const handleStatusChange = async (orderId, status) => {
    try {
      await updateOrderStatus(orderId, { status });
      setOrders((prev) =>
        prev.map((o) => (o._id === orderId ? { ...o, status } : o))
      );
    } catch (err) {
      console.error("Status update failed:", err);
    }
  };

  /* -------------------- DELETE ORDER -------------------- */
  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm("Are you sure you want to delete this order?")) return;

    try {
      await deleteOrder(orderId);
      setOrders((prev) => prev.filter((o) => o._id !== orderId));
    } catch (err) {
      console.error("Delete order failed:", err);
    }
  };

  const statusColor = (status) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-700";
      case "processing":
        return "bg-blue-100 text-blue-700";
      case "shipped":
        return "bg-purple-100 text-purple-700";
      case "delivered":
        return "bg-green-100 text-green-700";
      case "cancelled":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 font-['Poppins']">
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <AdminSidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
      />

      <div className="lg:ml-64 min-h-screen">
        <AdminHeader onMenuClick={() => setIsSidebarOpen(true)} />

        <div className="p-6 space-y-6">
          {/* HEADER */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                Orders Management
              </h2>
              <p className="text-sm text-gray-500">
                Manage, update and track customer orders
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                placeholder="Search by ID, name or email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-black/20"
              />

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-black/20"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          {/* TABLE */}
          {loading ? (
            <div className="text-center py-20 text-gray-500">
              Loading orders...
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-2xl shadow-sm border border-dashed">
              No orders found
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 text-gray-600 uppercase text-xs">
                  <tr>
                    <th className="p-4 text-left">Order</th>
                    <th className="p-4 text-left">Customer</th>
                    <th className="p-4 text-left">Total</th>
                    <th className="p-4 text-left">Status</th>
                    <th className="p-4 text-left">Date</th>
                    <th className="p-4 text-left">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {filtered.map((order) => (
                    <tr key={order._id} className="border-t hover:bg-gray-50">
                      <td className="p-4 font-medium">
                        #{order._id.slice(-6)}
                      </td>

                      <td className="p-4">
                        {order.firstName
                          ? `${order.firstName} ${order.lastName}`
                          : "Guest"}
                      </td>

                      <td className="p-4 font-semibold">
                        ₦{order.total?.toLocaleString()}
                      </td>

                      <td className="p-4">
                        <select
                          value={order.status}
                          onChange={(e) =>
                            handleStatusChange(order._id, e.target.value)
                          }
                          className={`px-3 py-1 rounded-full text-xs font-medium ${statusColor(
                            order.status
                          )}`}
                        >
                          <option value="pending">Pending</option>
                          <option value="processing">Processing</option>
                          <option value="shipped">Shipped</option>
                          <option value="delivered">Delivered</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </td>

                      <td className="p-4">
                        {new Date(order.createdAt).toLocaleDateString()}
                      </td>

                      <td className="p-4 flex gap-4">
                        <button
                          onClick={() => setSelectedOrder(order)}
                          className="text-blue-600 hover:underline text-sm"
                        >
                          View
                        </button>

                        <button
                          onClick={() => handleDeleteOrder(order._id)}
                          className="text-red-600 hover:underline text-sm"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ---------------- ORDER DETAILS MODAL ---------------- */}
{selectedOrder && (
  <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-y-auto">

      {/* HEADER */}
      <div className="flex justify-between items-center border-b p-6">
        <div>
          <h3 className="text-2xl font-bold text-gray-800">
            Order Details
          </h3>
          <p className="text-sm text-gray-500">
            Complete order information and payment summary
          </p>
        </div>

        <button
          onClick={() => setSelectedOrder(null)}
          className="text-gray-500 hover:text-black text-2xl"
        >
          ✕
        </button>
      </div>

      <div className="p-8 space-y-10">

        {/* ORDER SUMMARY CARDS */}
        <div className="grid md:grid-cols-4 gap-6">
          <div className="bg-gray-50 rounded-2xl p-5">
            <p className="text-xs text-gray-400">Order ID</p>
            <p className="font-semibold break-all text-sm">
              {selectedOrder._id}
            </p>
          </div>

          <div className="bg-gray-50 rounded-2xl p-5">
            <p className="text-xs text-gray-400">Order Status</p>
            <p className="font-semibold capitalize text-sm">
              {selectedOrder.status}
            </p>
          </div>

          <div className="bg-gray-50 rounded-2xl p-5">
            <p className="text-xs text-gray-400">Payment Status</p>
            <p className="font-semibold capitalize text-sm">
              {selectedOrder.paymentStatus}
            </p>
          </div>

          <div className="bg-gray-50 rounded-2xl p-5">
            <p className="text-xs text-gray-400">Order Type</p>
            <p className="font-semibold text-sm">
              {selectedOrder.user ? "Registered User" : "Guest Checkout"}
            </p>
          </div>
        </div>

        {/* CUSTOMER + SHIPPING */}
        <div className="grid md:grid-cols-2 gap-10">

          {/* CUSTOMER INFO */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-gray-800">
              Customer Information
            </h4>

            <div className="space-y-2 text-sm text-gray-700">
              <p>
                <strong>Name:</strong>{" "}
                {selectedOrder.firstName} {selectedOrder.lastName}
              </p>
              <p>
                <strong>Email:</strong> {selectedOrder.email}
              </p>
              <p>
                <strong>Phone:</strong> {selectedOrder.phone}
              </p>
              {selectedOrder.guestId && (
                <p>
                  <strong>Guest ID:</strong> {selectedOrder.guestId}
                </p>
              )}
            </div>
          </div>

          {/* SHIPPING */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-gray-800">
              Shipping Address
            </h4>

            <div className="space-y-2 text-sm text-gray-700">
              <p><strong>Address:</strong> {selectedOrder.address}</p>
              <p><strong>City:</strong> {selectedOrder.city}</p>
              <p><strong>State:</strong> {selectedOrder.state}</p>
            </div>
          </div>
        </div>

        {/* PAYMENT DETAILS */}
        <div>
          <h4 className="text-lg font-semibold mb-4 text-gray-800">
            Payment Details
          </h4>

          <div className="grid md:grid-cols-2 gap-8 text-sm text-gray-700">
            <div className="space-y-2">
              <p>
                <strong>Method:</strong> {selectedOrder.paymentMethod}
              </p>
              <p>
                <strong>Reference:</strong> {selectedOrder.paystackReference}
              </p>
              <p>
                <strong>Authorization URL:</strong>
              </p>
              <a
                href={selectedOrder.paystackAuthUrl}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 underline break-all"
              >
                {selectedOrder.paystackAuthUrl}
              </a>
            </div>

            <div className="bg-gray-50 rounded-2xl p-5 space-y-2">
              <p>
                Subtotal: ₦{selectedOrder.subtotal?.toLocaleString()}
              </p>
              <p>
                Shipping Fee: ₦{selectedOrder.shippingFee?.toLocaleString()}
              </p>
              <p className="text-lg font-bold pt-2 border-t">
                Total: ₦{selectedOrder.total?.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        {/* ORDER ITEMS */}
        <div>
          <h4 className="text-lg font-semibold mb-6 text-gray-800">
            Ordered Items
          </h4>

          <div className="space-y-6">
            {selectedOrder.items?.map((item) => (
              <div
                key={item._id}
                className="flex gap-6 border rounded-2xl p-6 items-center"
              >
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-24 h-24 object-cover rounded-xl border"
                />

                <div className="flex-1 space-y-1">
                  <p className="font-semibold text-gray-800 text-lg">
                    {item.name}
                  </p>
                  <p className="text-sm text-gray-500">
                    Size: {item.size}
                  </p>
                  <p className="text-sm text-gray-500">
                    Quantity: {item.quantity}
                  </p>
                  <p className="text-sm text-gray-500">
                    Unit Price: ₦{item.price?.toLocaleString()}
                  </p>
                </div>

                <div className="text-right">
                  <p className="text-sm text-gray-500">Item Total</p>
                  <p className="font-bold text-lg">
                    ₦{(item.price * item.quantity).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* TIMELINE */}
        <div className="border-t pt-6 text-sm text-gray-500 space-y-2">
          <p>
            <strong>Created:</strong>{" "}
            {new Date(selectedOrder.createdAt).toLocaleString()}
          </p>
          <p>
            <strong>Last Updated:</strong>{" "}
            {new Date(selectedOrder.updatedAt).toLocaleString()}
          </p>
        </div>

      </div>
    </div>
  </div>
)}
    </div>
  );
}
